import React, {useCallback, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';

import {Link as ContentfulLink} from '@amblique/king-living/app/contentful/models/link';
import {
  Box, Popover, PopoverAnchor, PopoverContent, Portal, useMediaQuery, useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {ensureArray} from '@amblique/king-living/app/utils/utils';
import {FOCUSABLE_SELECTOR} from '@amblique/king-living/app/hooks/use-overlay-initial-focus';
import {MenuItemContent} from '@amblique/king-living/app/components/mega-menu/shared/menu-item-container';
import {useHeaderContext} from '@amblique/king-living/app/contexts/header-context';

const hasSubmenu = (menuItem) => {
  return ensureArray(menuItem?.submenuCollection?.items).filter(Boolean).length > 0;
};

// Nav triggers only use the link URL — not CMS link type styling (filled/outlined variants).
const toNavTriggerLink = (link) => {
  if (!link) {
    return null;
  }

  return {
    ...link,
    linkType: undefined,
    type: undefined,
  };
};

const navTriggerLinkSx = {
  _hover: {
    textDecoration: 'none !important',
  },
};

const POPOVER_MODIFIERS = [
  {
    name: 'offset',
    options: {
      offset: [0, 0],
    },
  },
  {
    name: 'computeStyles',
    options: {
      gpuAcceleration: false,
    },
  }
];

const getVisibleFocusables = (container) => {
  if (!container) {
    return [];
  }

  return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter((el) => {
    if (el.hasAttribute('disabled') || el.getAttribute('aria-hidden') === 'true') {
      return false;
    }

    return el.getClientRects().length > 0;
  });
};

const MegaMenuTriggerComponent = ({
  focusPanelOnOpen = false,
  isOpen = false,
  menuId,
  menuItem,
  navigationMenu,
  onClose,
  onFocusAdjacentTrigger,
  onForceClose,
  onKeyboardOpen,
  onOpen,
  onToggle,
  ...props
}) => {
  const [popoverTop, setPopoverTop] = useState(0);
  const [isDesktop] = useMediaQuery('(min-width: 992px)');
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const headerWrapperRef = useRef(null);
  // Get headerStyle from context for nav item color variant
  const {headerStyle, isSearchOpen} = useHeaderContext();
  // Use headerStyle as variant for consistent color scheme with Header
  const styles = useMultiStyleConfig('MegaMenu', {isDesktop, isOpen, isSearchOpen, popoverTop, variant: headerStyle});

  const focusTrigger = useCallback(() => {
    const triggerEl = triggerRef.current;

    if (!triggerEl) {
      return;
    }

    if (triggerEl.matches?.(FOCUSABLE_SELECTOR)) {
      triggerEl.focus({preventScroll: true});
      return;
    }

    triggerEl.querySelector(FOCUSABLE_SELECTOR)?.focus({preventScroll: true});
  }, []);

  const focusFirstPanelItem = useCallback(() => {
    const focusFirst = () => {
      getVisibleFocusables(panelRef.current)[0]?.focus({preventScroll: true});
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(focusFirst);
    });
  }, []);

  useEffect(() => {
    if (!isOpen || !isDesktop) {
      return;
    }

    const updatePopoverPosition = () => {
      const triggerButton = triggerRef.current;

      if (!headerWrapperRef.current) {
        headerWrapperRef.current = document.querySelector('[data-header-wrapper]');
      }
      const headerWrapper = headerWrapperRef.current;

      if (triggerButton) {
        // Find the parent nav element (megaMenuDesktopNav) which includes padding and border
        const navElement = triggerButton.closest('nav');

        if (navElement) {
          const navRect = navElement.getBoundingClientRect();

          // Position popover below nav bottom (which includes the 1px border)
          // No additional offset needed since nav now sits flush at header bottom
          setPopoverTop(navRect.bottom);
        } else {
          // Fallback: Use trigger bottom if nav not found
          const triggerRect = triggerButton.getBoundingClientRect();

          setPopoverTop(triggerRect.bottom);
        }
      } else if (headerWrapper) {
        const height = headerWrapper.getBoundingClientRect().height;

        setPopoverTop(height);
      }
    };

    // Use requestAnimationFrame to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      updatePopoverPosition();
    }, 0);

    // Update on resize
    window.addEventListener('resize', updatePopoverPosition);
    window.addEventListener('scroll', updatePopoverPosition, true);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updatePopoverPosition);
      window.removeEventListener('scroll', updatePopoverPosition, true);
    };
  }, [isOpen, isDesktop, menuItem?.sys?.id]);

  // Global Escape key handler - close menu when Escape is pressed anywhere
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleGlobalKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onForceClose?.();
        focusTrigger();
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);

    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown);
    };
  }, [focusTrigger, isOpen, onForceClose]);

  useEffect(() => {
    if (!isOpen || !focusPanelOnOpen || !isDesktop) {
      return undefined;
    }

    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const firstFocusable = panelRef.current?.querySelector(FOCUSABLE_SELECTOR);

        firstFocusable?.focus({preventScroll: true});
      });
    });

    return () => cancelAnimationFrame(id);
  }, [focusPanelOnOpen, isDesktop, isOpen]);

  const handleTriggerFocus = useCallback((event) => {
    if (isSearchOpen) {
      return;
    }

    // Ignore focus moving within the same trigger wrapper.
    if (event.currentTarget.contains(event.relatedTarget)) {
      return;
    }

    event.currentTarget.dataset.menuId = menuId;
    onKeyboardOpen?.(event);
  }, [isSearchOpen, menuId, onKeyboardOpen]);

  const handleButtonKeyDown = useCallback((event) => {
    if (event.key === 'Tab' && !event.shiftKey && (isOpen || hasSubmenu(menuItem))) {
      event.preventDefault();
      if (!isOpen) {
        event.currentTarget.dataset.menuId = menuId;
        onKeyboardOpen?.(event);
      }
      focusFirstPanelItem();
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (isOpen) {
        focusFirstPanelItem();
      } else {
        onToggle?.(event);
      }
    } else if (event.key === 'Escape' && isOpen) {
      event.preventDefault();
      onForceClose?.();
    }
  }, [focusFirstPanelItem, isOpen, menuId, menuItem, onForceClose, onKeyboardOpen, onToggle]);

  // Link trigger: Tab opens submenu path into panel; Enter keeps native redirect.
  const handleLinkKeyDown = useCallback((event) => {
    if (event.key === 'Tab' && !event.shiftKey) {
      event.preventDefault();
      if (!isOpen) {
        event.currentTarget.dataset.menuId = menuId;
        onKeyboardOpen?.(event);
      }
      focusFirstPanelItem();
      return;
    }

    if (event.key === 'Escape' && isOpen) {
      event.preventDefault();
      onForceClose?.();
      focusTrigger();
    }
  }, [focusFirstPanelItem, focusTrigger, isOpen, menuId, onForceClose, onKeyboardOpen]);

  const handlePanelKeyDown = useCallback((event) => {
    if (event.key !== 'Tab') {
      return;
    }

    const focusables = getVisibleFocusables(panelRef.current);

    if (!focusables.length) {
      return;
    }

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;

    if (!event.shiftKey && active === last) {
      event.preventDefault();
      onFocusAdjacentTrigger?.(menuId, 'next');
      return;
    }

    if (event.shiftKey && active === first) {
      event.preventDefault();
      focusTrigger();
    }
  }, [focusTrigger, menuId, onFocusAdjacentTrigger]);

  // Wrapper to set data attribute before calling parent handler
  const handleMouseEnter = useCallback((event) => {
    if (isSearchOpen) {
      return;
    }

    event.currentTarget.dataset.menuId = menuId;
    onOpen?.(event);
  }, [isSearchOpen, menuId, onOpen]);

  const handlePanelLinkClickCapture = useCallback((event) => {
    if (event.target.closest('a[href]')) {
      onForceClose?.();
    }
  }, [onForceClose]);

  const submenuItems = ensureArray(menuItem?.submenuCollection?.items).filter(Boolean);
  const hasSubmenuItems = hasSubmenu(menuItem);
  const link = menuItem?.link;

  if (!hasSubmenuItems) {
    return (
      <Box {...props} data-mega-menu-trigger={menuId} data-menu-id={menuId} onMouseEnter={onClose}>
        <Box
          as="span"
          className={link ? 'menu-item-wrapper' : 'menu-item'}
          display={link ? 'inline-block' : undefined}
          position={link ? 'relative' : undefined}
          {...styles.desktopTriggerButton}
        >
          {link ? (
            <ContentfulLink
              className="menu-item"
              component={toNavTriggerLink(link)}
              sx={navTriggerLinkSx}
            >
              {menuItem?.type === 'sale' && <Box as="span" {...styles.saleBadge} />}
              {menuItem.title}
            </ContentfulLink>
          ) : (
            <>
              {menuItem?.type === 'sale' && <Box as="span" {...styles.saleBadge} />}
              {menuItem.title}
            </>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box {...props}>
      <Popover
        closeOnBlur={false}
        isLazy={true}
        isOpen={isOpen && !isSearchOpen}
        lazyBehavior="keepMounted"
        matchWidth={false}
        modifiers={POPOVER_MODIFIERS}
        placement="bottom-start"
        variant="fullWidth"
      >
        <PopoverAnchor>
          {link ? (
            <Box
              ref={triggerRef}
              aria-expanded={isOpen}
              aria-haspopup="true"
              as="span"
              className="menu-item-wrapper"
              data-mega-menu-trigger={menuId}
              data-menu-id={menuId}
              display="inline-block"
              position="relative"
              {...styles.desktopTriggerButton}
              onFocus={handleTriggerFocus}
              onKeyDown={handleLinkKeyDown}
              onMouseEnter={handleMouseEnter}
            >
              <ContentfulLink
                className="menu-item"
                component={toNavTriggerLink(link)}
                sx={navTriggerLinkSx}
              >
                {menuItem?.type === 'sale' && <Box as="span" {...styles.saleBadge} />}
                {menuItem.title}
              </ContentfulLink>
            </Box>
          ) : (
            <Box
              ref={triggerRef}
              aria-expanded={isOpen}
              aria-haspopup="true"
              as="button"
              className="menu-item"
              data-mega-menu-trigger={menuId}
              data-menu-id={menuId}
              type="button"
              {...styles.desktopTriggerButton}
              onFocus={handleTriggerFocus}
              onKeyDown={handleButtonKeyDown}
              onMouseEnter={handleMouseEnter}
            >
              {menuItem?.type === 'sale' && (<Box as="span" {...styles.saleBadge} />)}
              {menuItem.title}
            </Box>
          )}
        </PopoverAnchor>
        <Portal>
          <PopoverContent
            ref={panelRef}
            rootProps={{zIndex: isSearchOpen ? 1 : 1600}}
            {...styles.desktopPopoverContent}
            data-mega-menu-panel={menuId}
            data-menu-id={menuId}
            onClickCapture={handlePanelLinkClickCapture}
            onKeyDown={handlePanelKeyDown}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={onClose}
          >
            <MenuItemContent isMobile={false} menuItem={menuItem} submenuItems={submenuItems} />
          </PopoverContent>
        </Portal>
      </Popover>
    </Box>
  );
};

MegaMenuTriggerComponent.displayName = 'MegaMenuTrigger';
MegaMenuTriggerComponent.propTypes = {
  focusPanelOnOpen: PropTypes.bool,
  isOpen: PropTypes.bool,
  menuId: PropTypes.string.isRequired,
  menuItem: PropTypes.object.isRequired,
  navigationMenu: PropTypes.object,
  onClose: PropTypes.func,
  onFocusAdjacentTrigger: PropTypes.func,
  onForceClose: PropTypes.func,
  onKeyboardOpen: PropTypes.func,
  onOpen: PropTypes.func,
  onToggle: PropTypes.func,
};

/**
 * Memoized MegaMenuTrigger component.
 * Only re-renders when props actually change.
 *
 * Custom comparison function optimizes for:
 * - isOpen: boolean comparison (changes when menu opens/closes)
 * - menuId: string comparison (stable)
 * - menuItem: compares by sys.id to handle object reference changes
 * - Function props: reference comparison (all stable via useCallback)
 * - navigationMenu: reference comparison (stable)
 */
export const MegaMenuTrigger = React.memo(MegaMenuTriggerComponent, (prevProps, nextProps) => {
  // Quick reference equality check for all props
  if (
    prevProps.focusPanelOnOpen === nextProps.focusPanelOnOpen &&
    prevProps.isOpen === nextProps.isOpen &&
    prevProps.menuId === nextProps.menuId &&
    prevProps.menuItem?.sys?.id === nextProps.menuItem?.sys?.id &&
    prevProps.navigationMenu === nextProps.navigationMenu &&
    prevProps.onClose === nextProps.onClose &&
    prevProps.onFocusAdjacentTrigger === nextProps.onFocusAdjacentTrigger &&
    prevProps.onForceClose === nextProps.onForceClose &&
    prevProps.onKeyboardOpen === nextProps.onKeyboardOpen &&
    prevProps.onOpen === nextProps.onOpen &&
    prevProps.onToggle === nextProps.onToggle
  ) {
    // Props are equal, skip re-render
    return true;
  }

  // Props changed, allow re-render
  return false;
});
